// D14's write-ahead queue for logged sets: a set is written here the moment
// the person taps Log, sent from here, and removed only when the server
// has acknowledged it — so a dropped connection, a reload or a locked
// phone never loses a set. Resending is safe: each set carries its own
// client_id and the server answers a repeat with the stored row.
//
// One queue per person and session: `mono.training.<userId>.queue.<sessionId>`.
// A browser that refuses storage degrades to an in-memory queue for this
// visit (`persisted` turns false — say so on screen).
import { CLIENT_ID_RE } from "./clientId";
import { readStored, storedKeys, trainingKey, writeStored } from "./storage";

/** A queued set the server could take: an exercise key and a client_id. */
export function isQueuedSet(item) {
  return (
    item !== null &&
    typeof item === "object" &&
    !Array.isArray(item) &&
    typeof item.exercise === "string" &&
    typeof item.client_id === "string" &&
    CLIENT_ID_RE.test(item.client_id)
  );
}

export function setQueueKey(userId, sessionId) {
  if (sessionId === null || sessionId === undefined || sessionId === "") {
    throw new TypeError("A set queue needs the session's id.");
  }
  return trainingKey(userId, "queue", sessionId);
}

/**
 * The queue for one person's session.
 *
 * - `enqueue(set)` adds a set (a `TrainingSetWrite`: exercise, reps, load,
 *   rir, …, client_id), or replaces the queued one with the same client_id
 *   in place (an edit before it was acknowledged). Returns true when it
 *   reached localStorage.
 * - `pending()` the queued sets, oldest first. Junk entries (no client_id,
 *   not an object) are dropped on read.
 * - `ack(clientIds)` removes the acknowledged sets; returns what is left.
 * - `clear()` empties it (the session was discarded).
 * - `markDiscarding()` notes, beside the queue, that mono has been asked to
 *   discard the session (only while sets are queued): its answer can be
 *   lost to a reload or a closed tab, and should the session turn out gone,
 *   the sets went with it as asked — sendLeftovers lets them go unreported.
 *   `unmarkDiscarding()` when mono answers that it kept it; `discarding`
 *   reads the mark. The mark goes with the last queued set (an ack or a
 *   clear).
 * - `persisted` is false while storage refuses this queue's writes.
 */
export function createSetQueue(userId, sessionId) {
  const key = setQueueKey(userId, sessionId);
  const markKey = trainingKey(userId, "discarding", sessionId);
  let persisted = true;

  const read = () => {
    const value = readStored(key);
    return Array.isArray(value) ? value.filter(isQueuedSet) : [];
  };
  const save = (items) => {
    persisted = writeStored(key, items.length ? items : null);
    if (!items.length) writeStored(markKey, null); // nothing left to mark
    return persisted;
  };

  return {
    key,
    enqueue(item) {
      if (!isQueuedSet(item)) {
        throw new TypeError("A queued set needs an exercise and a valid client_id.");
      }
      const items = read();
      const at = items.findIndex((queued) => queued.client_id === item.client_id);
      if (at === -1) items.push(item);
      else items[at] = item;
      return save(items);
    },
    pending() {
      return read();
    },
    ack(clientIds) {
      const done = new Set([].concat(clientIds ?? []));
      const items = read();
      const left = items.filter((queued) => !done.has(queued.client_id));
      if (left.length !== items.length) save(left);
      return left;
    },
    clear() {
      save([]);
    },
    markDiscarding() {
      if (read().length) writeStored(markKey, true);
    },
    unmarkDiscarding() {
      writeStored(markKey, null);
    },
    get discarding() {
      return readStored(markKey) === true;
    },
    get persisted() {
      return persisted;
    },
  };
}

/**
 * The sessions of this person's that still have queued sets — e.g. a
 * session that closed itself (D18) before its last sets got through. The
 * API takes sets into a finished session, so they can still be sent.
 */
export function queuedSessionIds(userId) {
  const prefix = `${trainingKey(userId, "queue")}.`;
  return storedKeys(prefix)
    .filter((key) => {
      const value = readStored(key);
      return Array.isArray(value) && value.some(isQueuedSet);
    })
    .map((key) => {
      const id = key.slice(prefix.length);
      return /^\d+$/.test(id) ? Number(id) : id;
    });
}
